import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Image,
  Center,
  Flex,
  Switch,
  Divider
} from '@chakra-ui/react';
import { FiArrowLeft, FiUpload, FiX, FiPlus, FiVideo } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { UploadBusinessLogic } from '../services/uploadBusinessLogic';
import { useUploadState } from '../hooks/useUploadState';

const UploadMovie: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const {
    formData,
    uploadState,
    updateFormData,
    updateUploadState,
    addError,
    clearErrors,
    resetForm
  } = useUploadState();

  const [currentStep, setCurrentStep] = useState(1);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const handleInputChange = (field: string, value: any) => {
    updateFormData(field, value);
    if (uploadState.errors[field]) {
      updateUploadState({
        errors: { ...uploadState.errors, [field]: '' }
      });
    }
  };

  const handleGenreToggle = (genre: string) => {
    const currentGenres = formData.genres;
    const isSelected = currentGenres.includes(genre as any);
    
    if (isSelected) {
      updateFormData('genres', currentGenres.filter(g => g !== genre));
    } else if (currentGenres.length < 3) {
      updateFormData('genres', [...currentGenres, genre as any]);
    }
  };

  const handlePosterSelect = (file: File) => {
    updateFormData('posterFile', file);
    const url = URL.createObjectURL(file);
    updateFormData('posterUrl', url);
  };

  const handlePosterRemove = () => {
    updateFormData('posterFile', null);
    updateFormData('posterUrl', '');
  };

  const handleTrailerSelect = (file: File) => {
    updateFormData('trailerFile', file);
    const url = URL.createObjectURL(file);
    updateFormData('trailerUrl', url);
  };

  const handleTrailerRemove = () => {
    updateFormData('trailerFile', null);
    updateFormData('trailerUrl', '');
  };

  const handleSubtitleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newSubtitles = files.map(file => ({
        file,
        language: 'English', // Default language, could be made configurable
        id: Math.random().toString(36).substr(2, 9)
      }));
      updateFormData('subtitleFiles', [...formData.subtitleFiles, ...newSubtitles]);
    }
  };

  const handleSubtitleRemove = (index: number) => {
    const newSubtitles = formData.subtitleFiles.filter((_, i) => i !== index);
    updateFormData('subtitleFiles', newSubtitles);
  };

  const handleFileSelect = async (files: File[]) => {
    if (!user) return;
    
    updateUploadState({
      uploadedFiles: [...uploadState.uploadedFiles, ...files]
    });

    try {
      await UploadBusinessLogic.handleFileUpload(
        files,
        user.uid,
        (progress) => updateUploadState({ uploadProgress: progress }),
        (url) => updateUploadState({
          uploadedFileUrls: [...uploadState.uploadedFileUrls, url]
        }),
        (error) => addError('files', error)
      );
    } catch (error) {
      console.error('Error uploading files:', error);
      addError('files', 'Failed to upload files');
    }
  };

  const handleFileRemove = (index: number) => {
    const newFiles = uploadState.uploadedFiles.filter((_, i) => i !== index);
    const newUrls = uploadState.uploadedFileUrls.filter((_, i) => i < newFiles.length);
    updateUploadState({
      uploadedFiles: newFiles,
      uploadedFileUrls: newUrls
    });
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.title.trim()) {
        addError('title', 'Title is required');
        return;
      }
      if (!formData.description.trim()) {
        addError('description', 'Description is required');
        return;
      }
      if (formData.genres.length === 0) {
        addError('genres', 'Please select at least one genre');
        return;
      }
    }
    setCurrentStep(2);
    // Scroll to top when moving to next step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setCurrentStep(1);
    // Scroll to top when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!user) return;

    clearErrors();
    updateUploadState({ isUploading: true, uploadProgress: 0 });

    try {
      // First, ensure all files are uploaded
      let finalUploadedFileUrls = [...uploadState.uploadedFileUrls];
      
      if (uploadState.uploadedFiles.length > 0 && uploadState.uploadedFileUrls.length === 0) {
        const uploadedUrls: string[] = [];
        
        for (let i = 0; i < uploadState.uploadedFiles.length; i++) {
          const file = uploadState.uploadedFiles[i];
          const { uploadFileDirect } = await import('../services/uploadService');
          const url = await uploadFileDirect(
            `movies/videos/${user.uid}`, 
            file,
            (progress) => {
              // Update progress for file upload phase (0-30% for file uploads)
              const fileProgress = Math.round((((i + progress / 100) / uploadState.uploadedFiles.length) * 30));
              updateUploadState({ uploadProgress: fileProgress });
            }
          );
          uploadedUrls.push(url);
        }
        
        finalUploadedFileUrls = uploadedUrls;
        updateUploadState({ uploadedFileUrls: uploadedUrls });
      }

      const errors = UploadBusinessLogic.validateForm(
        { ...formData, category: 'movie' },
        uploadState.uploadedFiles,
        finalUploadedFileUrls
      );

      if (Object.keys(errors).length > 0) {
        updateUploadState({ errors, isUploading: false });
        return;
      }

      const contentId = await UploadBusinessLogic.handleSingleContentUpload(
        { ...formData, category: 'movie' },
        user.uid,
        uploadState.uploadedFiles,
        finalUploadedFileUrls,
        (progress) => {
          // Add 30% to account for file upload phase
          updateUploadState({ uploadProgress: 30 + Math.round(progress * 0.7) });
        }
      );

      toast({
        title: 'Movie uploaded successfully!',
        description: 'Your movie has been published.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      resetForm();
      navigate(`/content/${contentId}`);
    } catch (error) {
      console.error('Error uploading movie:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your movie. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      updateUploadState({ isUploading: false });
    }
  };

  const renderStep1 = () => (
    <VStack gap={6} align="stretch">
      {/* Title */}
      <FormControl isInvalid={!!uploadState.errors.title}>
        <FormLabel color={labelColor} fontWeight="bold">
          Movie Title *
        </FormLabel>
        <Input
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter movie title"
          bg={inputBg}
          borderColor={borderColor}
        />
      </FormControl>

      {/* Description */}
      <FormControl isInvalid={!!uploadState.errors.description}>
        <FormLabel color={labelColor} fontWeight="bold">
          Description *
        </FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter movie description/synopsis"
          bg={inputBg}
          borderColor={borderColor}
          rows={4}
        />
      </FormControl>

      {/* Genres */}
      <FormControl isInvalid={!!uploadState.errors.genres}>
        <FormLabel color={labelColor} fontWeight="bold">
          Genres * (Select up to 3)
        </FormLabel>
        <Flex wrap="wrap" gap={2}>
          {GENRES.map(genre => (
            <Button
              key={genre}
              size="sm"
              variant={formData.genres.includes(genre as any) ? 'solid' : 'outline'}
              colorScheme="purple"
              onClick={() => handleGenreToggle(genre)}
              isDisabled={!formData.genres.includes(genre as any) && formData.genres.length >= 3}
            >
              {genre}
            </Button>
          ))}
        </Flex>
      </FormControl>

      {/* Cast, Director, Writer */}
      <HStack gap={4} align="start">
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Cast
          </FormLabel>
          <Input
            value={formData.cast}
            onChange={(e) => handleInputChange('cast', e.target.value)}
            placeholder="Actor names (comma separated)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Director
          </FormLabel>
          <Input
            value={formData.director}
            onChange={(e) => handleInputChange('director', e.target.value)}
            placeholder="Director name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Writer
          </FormLabel>
          <Input
            value={formData.writer}
            onChange={(e) => handleInputChange('writer', e.target.value)}
            placeholder="Writer name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
      </HStack>

      {/* Content Rating */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Content Rating
        </FormLabel>
        <RadioGroup
          value={formData.rating}
          onChange={(value) => handleInputChange('rating', value)}
        >
          <HStack gap={4} wrap="wrap">
            {CONTENT_RATINGS.map(rating => (
              <Radio key={rating.value} value={rating.value} colorScheme="purple">
                {rating.label}
              </Radio>
            ))}
          </HStack>
        </RadioGroup>
      </FormControl>

      <Divider my={4} />

      {/* Content Settings */}
      <Box p={4} bg={inputBg} borderRadius="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color={labelColor}>
          Content Settings
        </Text>
        <VStack gap={4} align="stretch">
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel mb={1} color={labelColor} fontWeight="bold">Enable Comments</FormLabel>
              <Text fontSize="sm" color="gray.500">
                Allow viewers to comment on this content
              </Text>
            </Box>
            <Switch
              isChecked={formData.commentsEnabled !== false}
              onChange={(e) => handleInputChange('commentsEnabled', e.target.checked)}
              colorScheme="purple"
            />
          </FormControl>

          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel mb={1} color={labelColor} fontWeight="bold">Age Restriction</FormLabel>
              <Text fontSize="sm" color="gray.500">
                Restrict this content to viewers 18+ only
              </Text>
            </Box>
            <Switch
              isChecked={formData.ageRestriction === true}
              onChange={(e) => handleInputChange('ageRestriction', e.target.checked)}
              colorScheme="red"
            />
          </FormControl>

          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel mb={1} color={labelColor} fontWeight="bold">Require Donation to View</FormLabel>
              <Text fontSize="sm" color="gray.500">
                Viewers must make a donation to access this content (Premium users exempt)
              </Text>
            </Box>
            <Switch
              isChecked={formData.donationRequired === true}
              onChange={(e) => handleInputChange('donationRequired', e.target.checked)}
              colorScheme="orange"
            />
          </FormControl>
        </VStack>
      </Box>
    </VStack>
  );

  const renderStep2 = () => (
    <VStack gap={6} align="stretch">
      {/* Poster Upload */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Movie Poster
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={formData.posterFile ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg={formData.posterFile ? 'purple.50' : inputBg}
          _dark={{ bg: formData.posterFile ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('poster-upload')?.click()}
          minH="200px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {formData.posterFile ? (
            <VStack gap={3}>
              <Box position="relative">
                <Image
                  src={formData.posterUrl!}
                  alt="Poster preview"
                  maxH="150px"
                  maxW="200px"
                  objectFit="cover"
                  borderRadius="md"
                />
                <Button
                  position="absolute"
                  top={-2}
                  right={-2}
                  size="sm"
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePosterRemove();
                  }}
                >
                  <IconWrapper icon={FiX} size={12} />
                </Button>
              </Box>
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiPlus} size={32} color="gray.400" />
              <Text color="gray.500">Click to upload poster</Text>
            </VStack>
          )}
          <Input
            id="poster-upload"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePosterSelect(file);
            }}
            display="none"
          />
        </Box>
      </FormControl>

      {/* Trailer Upload */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Trailer (Optional)
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={formData.trailerFile ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg={formData.trailerFile ? 'purple.50' : inputBg}
          _dark={{ bg: formData.trailerFile ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('trailer-upload')?.click()}
          minH="120px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {formData.trailerFile ? (
            <VStack gap={3}>
              <Text color="purple.600" fontWeight="medium">
                {formData.trailerFile.name}
              </Text>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTrailerRemove();
                }}
              >
                Remove
              </Button>
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiVideo} size={24} color="gray.400" />
              <Text color="gray.500">Click to upload trailer</Text>
            </VStack>
          )}
          <Input
            id="trailer-upload"
            type="file"
            accept="video/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleTrailerSelect(file);
            }}
            display="none"
          />
        </Box>
      </FormControl>

      {/* Video Upload */}
      <FormControl isInvalid={!!uploadState.errors.files}>
        <FormLabel color={labelColor} fontWeight="bold">
          Movie Video *
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={uploadState.uploadedFiles.length > 0 ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg={uploadState.uploadedFiles.length > 0 ? 'purple.50' : inputBg}
          _dark={{ bg: uploadState.uploadedFiles.length > 0 ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('video-upload')?.click()}
          minH="120px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {uploadState.uploadedFiles.length > 0 ? (
            <VStack gap={3}>
              {uploadState.uploadedFiles.map((file, index) => (
                <HStack key={index} gap={2}>
                  <Text color="purple.600" fontWeight="medium">
                    {file.name}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileRemove(index);
                    }}
                  >
                    <IconWrapper icon={FiX} size={12} />
                  </Button>
                </HStack>
              ))}
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiUpload} size={24} color="gray.400" />
              <Text color="gray.500">Click to upload movie video</Text>
            </VStack>
          )}
          <Input
            id="video-upload"
            type="file"
            accept="video/*"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) handleFileSelect(files);
            }}
            display="none"
          />
        </Box>
      </FormControl>

      {/* Subtitles Upload */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Subtitles (Optional)
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={formData.subtitleFiles.length > 0 ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={6}
          textAlign="center"
          bg={formData.subtitleFiles.length > 0 ? 'purple.50' : inputBg}
          _dark={{ bg: formData.subtitleFiles.length > 0 ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('subtitle-upload')?.click()}
          minH="100px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {formData.subtitleFiles.length > 0 ? (
            <VStack gap={3}>
              {formData.subtitleFiles.map((subtitle, index) => (
                <HStack key={index} gap={2}>
                  <Text color="purple.600" fontWeight="medium" fontSize="sm">
                    {subtitle.file.name} ({subtitle.language})
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubtitleRemove(index);
                    }}
                  >
                    <IconWrapper icon={FiX} size={12} />
                  </Button>
                </HStack>
              ))}
              <Text fontSize="xs" color="gray.500">
                Click to add more subtitles
              </Text>
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiUpload} size={20} color="gray.400" />
              <Text color="gray.500" fontSize="sm">Click to upload subtitles</Text>
              <Text fontSize="xs" color="gray.400">.srt or .vtt files</Text>
            </VStack>
          )}
          <Input
            id="subtitle-upload"
            type="file"
            accept=".srt,.vtt,text/*"
            multiple
            onChange={handleSubtitleSelect}
            display="none"
          />
        </Box>
      </FormControl>
    </VStack>
  );

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to upload movies.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <HStack gap={4} align="center" maxW="1200px" mx="auto">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate('/upload')}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <VStack align="start" gap={1}>
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Upload Movie
            </Text>
            <Text color={labelColor}>
              Step {currentStep} of 2
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="800px" mx="auto">
        {currentStep === 1 ? renderStep1() : renderStep2()}

        {/* Progress Bar */}
        {uploadState.isUploading && (
          <VStack gap={2} mt={6}>
            <Text fontSize="sm" color={labelColor}>
              Uploading... {Math.round(uploadState.uploadProgress)}%
            </Text>
            <Progress
              value={uploadState.uploadProgress}
              size="sm"
              colorScheme="purple"
              w="100%"
              borderRadius="full"
            />
          </VStack>
        )}

        {/* Navigation Buttons */}
        <HStack gap={4} mt={8}>
          {currentStep > 1 && (
            <Button
              variant="outline"
              colorScheme="purple"
              onClick={prevStep}
              flex="1"
            >
              Back
            </Button>
          )}
          <Button
            colorScheme="purple"
            onClick={currentStep === 2 ? handleSubmit : nextStep}
            isLoading={uploadState.isUploading}
            loadingText="Uploading..."
            flex="1"
          >
            {currentStep === 2 ? 'Upload Movie' : 'Continue'}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default UploadMovie;
